import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { ActivateBankingCardDocument, CardDetailFragment } from 'generated/resources/graphql'

import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'
import app from 'modules/core/store/models/app'

import { canActivateCard } from 'modules/banking/lib/cards/canActivateCard'
import { isPending } from 'modules/banking/lib/cards/isPending'

import { SidebarSection } from 'modules/banking/components/CardSidepanel/_Content/_SidebarSection'

type ActivateCardFormValues = {
  code: string
  id: string
}

const useActivateCard = (card: CardDetailFragment) => {
  const [activateCard, { loading }] = useMutation(ActivateBankingCardDocument, {
    extractorFn: (data) => data.bankingMutations.activateBankingCard
  })

  const isValidCode = (code: string) => isNumber(code) && code.length === 4

  const form = useForm<ActivateCardFormValues>({
    defaultValues: {
      code: '',
      id: card.id + ''
    },
    validate: (data) => {
      const errors: FormErrors<ActivateCardFormValues> = {}
      const isValid = isValidCode(data.code)

      if (!data.code || !isValid) {
        errors.code = i18n.t('expenses_v2.cards.activation.invalid_number_error')
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await activateCard({ variables: values })
      const handled = handleResult(result, (errors) => {
        form.setErrors({
          code: errors['last4']
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('expenses_v2.cards.activation.success')
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error')
        })
      }
      return handled
    }
  })

  const readonly = !card._policies?.activate?.can
  const submitDisabled =
    readonly || !form.dirty || loading || form.isSubmitting || !!form.errors.code
  const onSubmit = () => form.handleSubmit()
  const onCodeChange = (value: string) => form.setFieldValue('code', value)

  return { form, submitDisabled, onSubmit, onCodeChange, readonly }
}

export const ActivateCard: React.FC<{ card: CardDetailFragment }> = ({ card }) => {
  const { form, submitDisabled, onSubmit, onCodeChange, readonly } = useActivateCard(card)

  if (!isPending(card) || !canActivateCard(card)) {
    return null
  }

  return (
    <SidebarSection>
      <Box width="full" paddingBottom="s24">
        <Box>
          <Text.Normal weight="semibold" size="300" color="textPrimary">
            {i18n.t('expenses_v2.cards.activation.activate_card')}
          </Text.Normal>
        </Box>

        <Box width="full" paddingTop="s12">
          <Text.Normal weight="normal" size="300" color="textSecondary">
            {i18n.t('expenses_v2.cards.activation.subtitle')}
          </Text.Normal>
        </Box>
      </Box>
      <Box gap="s24" width="full" paddingBottom="s12">
        <TextField
          type="password"
          label={i18n.t('expenses_v2.cards.activation.card_number')}
          onChange={onCodeChange}
          mandatory
          value={form.values.code}
          error={form.touched.code ? form.errors.code : undefined}
        />
      </Box>
      <Box width="full" flexDirection="rowReverse">
        <Button
          onClick={onSubmit}
          aria-label={i18n.t('expenses_v2.cards.actions.activate')}
          disabled={submitDisabled}
        >
          {i18n.t('expenses_v2.cards.actions.activate')}
        </Button>
      </Box>
    </SidebarSection>
  )
}
